import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Chip,
  Tooltip
} from '@mui/material';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import {
  DocumentTextIcon,
  PhotoIcon,
  DocumentIcon,
  TableCellsIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';
import ImagePreviewModal from '../Modal/ImagePreviewModal';
// Import file validation enums
const FileValidationEnum = require('../../enums/FileValidityEnums');

const LocalFileCard = ({ fileObj, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState('');
  const [displayType, setDisplayType] = useState('FILE');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (fileObj?.file) {
      const file = fileObj.file;
      const mimeType = file.type;
      
      // Extract file extension for display
      const extension = file.name.split('.').pop()?.toUpperCase() || 'FILE';
      setFileType(extension);

      // Determine display type based on MIME type and allowed types
      if (FileValidationEnum.ALLOWED_FILE_TYPES.includes(mimeType)) {
        if (mimeType.startsWith('image/')) {
          setDisplayType('IMAGE');
          // Create preview URL for images
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          
          // Cleanup URL when component unmounts
          return () => {
            URL.revokeObjectURL(url);
          };
        } else if (mimeType === 'application/pdf') {
          setDisplayType('PDF');
        } else if (
          mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          mimeType === 'application/msword'
        ) {
          setDisplayType('DOC');
        } else if (
          mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          mimeType === 'application/vnd.ms-excel' ||
          mimeType === 'text/csv'
        ) {
          setDisplayType('EXCEL');
        } else if (mimeType === 'text/plain') {
          setDisplayType('TEXT');
        } else {
          setDisplayType('DOC'); // Default for other allowed types
        }
      } else {
        setDisplayType('UNSUPPORTED');
      }
    }
  }, [fileObj]);

  const getFileIcon = () => {
    if (!fileObj?.file) return <DocumentIcon className="w-14 h-14 text-gray-400" />;

    switch (displayType) {
      case 'IMAGE':
        return <PhotoIcon className="w-14 h-14 text-purple-500" />;
      case 'PDF':
        return <DocumentTextIcon className="w-14 h-14 text-red-500" />;
      case 'DOC':
        return <DocumentTextIcon className="w-14 h-14 text-blue-500" />;
      case 'EXCEL':
        return <TableCellsIcon className="w-14 h-14 text-green-500" />;
      case 'TEXT':
        return <DocumentTextIcon className="w-14 h-14 text-gray-500" />;
      case 'UNSUPPORTED':
        return <DocumentIcon className="w-14 h-14 text-red-400" />;
      default:
        return <DocumentIcon className="w-14 h-14 text-gray-400" />;
    }
  };

  const getChipColor = () => {
    switch (displayType) {
      case 'PDF':
        return { backgroundColor: '#fef2f2', color: '#dc2626' };
      case 'DOC':
        return { backgroundColor: '#eff6ff', color: '#2563eb' };
      case 'EXCEL':
        return { backgroundColor: '#f0fdf4', color: '#16a34a' };
      case 'TEXT':
        return { backgroundColor: '#f8fafc', color: '#64748b' };
      case 'UNSUPPORTED':
        return { backgroundColor: '#fef2f2', color: '#ef4444' };
      default:
        return { backgroundColor: '#e2e8f0', color: '#475569' };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRemove = () => {
    if (onRemove && fileObj?.id) {
      onRemove(fileObj.id);
    }
  };

  const handlePreviewOpen = () => {
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          width: 280, // Fixed width
          height: 280, // Reduced height from 320 to 280
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
          border: displayType === 'UNSUPPORTED' ? '1px solid #fca5a5' : '1px solid #e2e8f0',
          borderRadius: 2,
        }}
      >
        {/* Action buttons - Remove and Preview */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            display: 'flex',
            gap: 0.5,
          }}
        >
          {/* Preview button - only for images */}
          {displayType === 'IMAGE' && previewUrl && (
            <Tooltip title="Preview Image" arrow>
              <IconButton
                size="small"
                onClick={handlePreviewOpen}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#6366f1',
                  '&:hover': {
                    backgroundColor: '#f0f9ff',
                    color: '#4f46e5',
                  },
                }}
              >
                <EyeIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Remove button */}
          <Tooltip title="Remove File" arrow>
            <IconButton
              size="small"
              onClick={handleRemove}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#ef4444',
                '&:hover': {
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                },
              }}
            >
              <XMarkIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Preview section - Fixed height */}
        <Box
          sx={{
            height: 200, // Keep preview area at 200px
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: displayType === 'UNSUPPORTED' ? '#fef2f2' : '#f8fafc',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {previewUrl && displayType === 'IMAGE' ? (
            // Image preview
            <CardMedia
              component="img"
              image={previewUrl}
              alt={fileObj?.name || 'File preview'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // Ensures image fits within the fixed dimensions
                cursor: 'pointer', // Indicate it's clickable
              }}
              onClick={handlePreviewOpen} // Allow clicking on image to preview
            />
          ) : (
            // Document icon with file type
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {getFileIcon()}
              <Chip
                label={displayType === 'UNSUPPORTED' ? 'UNSUPPORTED' : fileType}
                size="small"
                sx={{
                  ...getChipColor(),
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          )}
        </Box>

        {/* File details section - Dynamic height to fill remaining space */}
        <CardContent
          sx={{
            flex: 1, // Takes up remaining space
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // Center content vertically
            p: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <Box>
            <Tooltip title={fileObj?.name || 'Unknown file'} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: displayType === 'UNSUPPORTED' ? '#dc2626' : '#1e293b',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                {fileObj?.name || 'Unknown file'}
              </Typography>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                display: 'block',
              }}
            >
              {formatFileSize(fileObj?.size || 0)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        imageUrl={previewUrl}
        fileName={fileObj?.name}
        fileSize={fileObj?.size}
        fileType={displayType}
      />
    </>
  );
};

export default LocalFileCard; 